function clamp(n: number, min: number, max: number) {
 return Math.min(max, Math.max(min, n))
}

export function normalizeHex(hex: string): string | null {
 const v = String(hex || '').trim()
 const m = /^#([0-9a-fA-F]{6})$/.exec(v)
 if (!m) return null
 return `#${m[1].toUpperCase()}`
}

type Hsl = { h: number; s: number; l: number }

// Returns HSL as degrees + percentages, matching CSS var format in globals.css.
export function hexToHsl(hex: string): Hsl | null {
 const n = normalizeHex(hex)
 if (!n) return null

 const r = parseInt(n.slice(1, 3), 16) / 255
 const g = parseInt(n.slice(3, 5), 16) / 255
 const b = parseInt(n.slice(5, 7), 16) / 255

 const max = Math.max(r, g, b)
 const min = Math.min(r, g, b)
 const d = max - min

 let h = 0
 let s = 0
 const l = (max + min) / 2

 if (d !== 0) {
 s = d / (1 - Math.abs(2 * l - 1))
 switch (max) {
 case r:
 h = ((g - b) / d) % 6
 break
 case g:
 h = (b - r) / d + 2
 break
 default:
 h = (r - g) / d + 4
 break
 }
 h *= 60
 if (h < 0) h += 360
 }

 return {
 h: Math.round(clamp(h, 0, 360)),
 s: Math.round(clamp(s * 100, 0, 100)),
 l: Math.round(clamp(l * 100, 0, 100)),
 }
}

function hslToCss(hsl: Hsl): string {
 return `${Math.round(clamp(hsl.h, 0, 360))} ${Math.round(clamp(hsl.s, 0, 100))}% ${Math.round(clamp(hsl.l, 0, 100))}%`
}

function rotateHue(h: number, by: number) {
 const v = (h + by) % 360
 return v < 0 ? v + 360 : v
}

function mixToward(current: number, target: number, amount01: number) {
 const a = clamp(amount01, 0, 1)
 return current + (target - current) * a
}

function derive(hsl: Hsl, patch: Partial<Hsl>): Hsl {
 return {
 h: patch.h ?? hsl.h,
 s: patch.s ?? hsl.s,
 l: patch.l ?? hsl.l,
 }
}

function hexToRgb01(hex: string): { r: number; g: number; b: number } | null {
 const n = normalizeHex(hex)
 if (!n) return null
 return {
 r: parseInt(n.slice(1, 3), 16) / 255,
 g: parseInt(n.slice(3, 5), 16) / 255,
 b: parseInt(n.slice(5, 7), 16) / 255,
 }
}

function srgbToLinear(c: number) {
 return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
}

function relativeLuminance(hex: string): number | null {
 const rgb = hexToRgb01(hex)
 if (!rgb) return null
 const r = srgbToLinear(rgb.r)
 const g = srgbToLinear(rgb.g)
 const b = srgbToLinear(rgb.b)
 return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

function pickForegroundForPrimary(primaryHex: string): '0 0% 100%' | '222 47% 11%' {
 // Simple but effective: if primary is bright, use dark text; else use white.
 const lum = relativeLuminance(primaryHex)
 if (lum === null) return '0 0% 100%'
 return lum > 0.55 ? '222 47% 11%' : '0 0% 100%'
}

/** Default brand primary — matches globals.css `--primary` (navy #0F172A). */
export const DEFAULT_TENANT_PRIMARY_HEX = '#0F172A'

/** Properties written inline by applyPrimaryColor; removed when entering dark mode. */
export const TENANT_INJECTED_STYLE_PROPERTIES = [
 '--tenant-primary',
 '--tenant-primary-dark',
 '--tenant-primary-foreground',
 '--tenant-secondary',
 '--tenant-accent',
 '--tenant-muted',
 '--tenant-border',
 '--tenant-secondary-dark',
 '--tenant-accent-dark',
 '--tenant-muted-dark',
 '--tenant-border-dark',
 '--tenant-gradient-from ',
 '--tenant-gradient-to',
 '--tenant-glow',
 '--tenant-sidebar-background',
 '--tenant-sidebar-accent',
 '--tenant-sidebar-border',
 '--tenant-sidebar-background-dark',
 '--tenant-sidebar-accent-dark',
 '--tenant-sidebar-border-dark',
 '--primary',
 '--primary-foreground',
 '--ring',
 '--secondary',
 '--secondary-foreground',
 '--accent',
 '--accent-foreground',
 '--muted',
 '--muted-foreground',
 '--border',
 '--input',
 '--sidebar-background',
 '--sidebar-accent',
 '--sidebar-border',
 '--sidebar-ring',
] as const

export function clearTenantThemeInlineStyle(root: HTMLElement = document.documentElement): void {
 for (const prop of TENANT_INJECTED_STYLE_PROPERTIES) {
 root.style.removeProperty(prop)
 }
}

/** Reset UI to the design-system default palette in globals.css (no tenant inline overrides). */
export function applyDefaultTenantTheme(root: HTMLElement = document.documentElement): void {
  clearTenantThemeInlineStyle(root)
}

export function applyPrimaryColor(hex: string): boolean {
 if (typeof document === 'undefined') return false
 const normalized = normalizeHex(hex)
 const hsl = normalized ? hexToHsl(normalized) : null
 if (!normalized || !hsl) return false

 const root = document.documentElement
 // Debug / dark base theme: do not inject light-theme HSL into document while dark —
 // those values clash with globals.css `.dark` and look disjointed.
 if (root.classList.contains('dark')) {
 return false
 }

 // Store raw tenant brand color + derived tokens.
 const primary = hsl
 const primaryDark = derive(primary, { l: clamp(primary.l + 7, 35, 78) })
 const primaryFg = pickForegroundForPrimary(normalized)

 // Derived palettes (light)
 const secondary = derive(primary, { s: Math.round(mixToward(primary.s, 18, 0.75)), l: 94 })
 const accent = derive(primary, { s: Math.round(mixToward(primary.s, 28, 0.65)), l: 92 })
 const muted = derive(primary, { s: Math.round(mixToward(primary.s, 14, 0.85)), l: 94 })
 const border = derive(primary, { s: Math.round(mixToward(primary.s, 22, 0.7)), l: 88 })

 // Derived palettes (dark)
 const secondaryDark = derive(primary, { s: Math.round(mixToward(primary.s, 22, 0.8)), l: 18 })
 const accentDark = derive(primary, { s: Math.round(mixToward(primary.s, 26, 0.75)), l: 20 })
 const mutedDark = derive(primary, { s: Math.round(mixToward(primary.s, 16, 0.85)), l: 16 })
 const borderDark = derive(primary, { s: Math.round(mixToward(primary.s, 20, 0.85)), l: 20 })

 // Gradients / brand helpers
 const gradientFrom = derive(primary, { l: clamp(primary.l - 3, 0, 100) })
 const gradientTo = derive(primary, { h: rotateHue(primary.h, 18), s: clamp(primary.s - 8, 0, 100), l: clamp(primary.l - 8, 0, 100) })
 const glow = derive(primary, { l: clamp(primary.l, 0, 100) })

 // Sidebar helpers (light + dark)
 const sidebarBg = derive(primary, { s: Math.round(mixToward(primary.s, 55, 0.35)), l: 22 })
 const sidebarAccent = derive(primary, { s: Math.round(mixToward(primary.s, 45, 0.25)), l: 30 })
 const sidebarBorder = derive(primary, { s: Math.round(mixToward(primary.s, 40, 0.2)), l: 30 })

 const sidebarBgDark = derive(primary, { s: Math.round(mixToward(primary.s, 40, 0.35)), l: 5 })
 const sidebarAccentDark = derive(primary, { s: Math.round(mixToward(primary.s, 30, 0.45)), l: 14 })
 const sidebarBorderDark = derive(primary, { s: Math.round(mixToward(primary.s, 25, 0.55)), l: 12 })

 // Set tenant vars (consumed by globals.css as fallbacks)
 root.style.setProperty('--tenant-primary', hslToCss(primary))
 root.style.setProperty('--tenant-primary-dark', hslToCss(primaryDark))
 root.style.setProperty('--tenant-primary-foreground', primaryFg)

 root.style.setProperty('--tenant-secondary', hslToCss(secondary))
 root.style.setProperty('--tenant-accent', hslToCss(accent))
 root.style.setProperty('--tenant-muted', hslToCss(muted))
 root.style.setProperty('--tenant-border', hslToCss(border))

 root.style.setProperty('--tenant-secondary-dark', hslToCss(secondaryDark))
 root.style.setProperty('--tenant-accent-dark', hslToCss(accentDark))
 root.style.setProperty('--tenant-muted-dark', hslToCss(mutedDark))
 root.style.setProperty('--tenant-border-dark', hslToCss(borderDark))

 root.style.setProperty('--tenant-gradient-from ', hslToCss(gradientFrom))
 root.style.setProperty('--tenant-gradient-to', hslToCss(gradientTo))
 root.style.setProperty('--tenant-glow', hslToCss(glow))

 root.style.setProperty('--tenant-sidebar-background', hslToCss(sidebarBg))
 root.style.setProperty('--tenant-sidebar-accent', hslToCss(sidebarAccent))
 root.style.setProperty('--tenant-sidebar-border', hslToCss(sidebarBorder))
 root.style.setProperty('--tenant-sidebar-background-dark', hslToCss(sidebarBgDark))
 root.style.setProperty('--tenant-sidebar-accent-dark', hslToCss(sidebarAccentDark))
 root.style.setProperty('--tenant-sidebar-border-dark', hslToCss(sidebarBorderDark))

 // Backward-compatible tokens: keep existing vars in sync.
 root.style.setProperty('--primary', hslToCss(primary))
 root.style.setProperty('--primary-foreground', primaryFg)
 root.style.setProperty('--ring', '217 91% 53%')
 root.style.setProperty('--secondary', hslToCss(secondary))
 root.style.setProperty('--secondary-foreground', '222 47% 28%')
 root.style.setProperty('--accent', hslToCss(accent))
 root.style.setProperty('--accent-foreground', '222 47% 22%')
 root.style.setProperty('--muted', hslToCss(muted))
 root.style.setProperty('--muted-foreground', '215 16% 45%')
 root.style.setProperty('--border', hslToCss(border))
 root.style.setProperty('--input', hslToCss(border))

 root.style.setProperty('--sidebar-background', hslToCss(sidebarBg))
 root.style.setProperty('--sidebar-accent', hslToCss(sidebarAccent))
 root.style.setProperty('--sidebar-border', hslToCss(sidebarBorder))
 root.style.setProperty('--sidebar-ring', '217 91% 53%')

 return true
}
